import React from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FileUp, Loader2, X } from 'lucide-react';
import { podcastApi } from '@/lib/podcast-api';
import type { Podcast } from '@/lib/podcast-api';
import type { PodcastImportEditorLocationState } from '@/pages/podcasts/PodcastDocumentImportPage';
import { toast } from '@/components/ui/Toast';
import { cn } from '@/lib/utils';

const IMPORT_ACCEPT = 'application/pdf,.pdf,text/markdown,.md,.markdown,.mdown';

function isImportableDocument(file: File): boolean {
  const name = file.name.toLowerCase();
  if (name.endsWith('.pdf')) return true;
  if (/\.(md|markdown|mdown)$/.test(name)) return true;
  return file.type === 'application/pdf' || file.type === 'text/markdown' || file.type === 'text/x-markdown';
}

type Props = {
  podcast: Podcast;
  onClose: () => void;
  onImported: () => void;
};

/** Sélection du fichier puis redirection vers la page d’édition Markdown. */
export function PodcastPdfImportModal({ podcast, onClose }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [file, setFile] = React.useState<File | null>(null);
  const [loading, setLoading] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    if (!isImportableDocument(selected)) {
      toast.error(t('podcasts.pdfImport.invalidFile'));
      return;
    }
    setFile(selected);
    setLoading(true);
    try {
      const data = await podcastApi.previewPdfImport(podcast.id, selected);
      const markdown =
        data.generatedMarkdown ?? (await selected.text());
      const state: PodcastImportEditorLocationState = {
        markdown,
        documentTitle: data.documentTitle,
        pageCount: data.pageCount,
        sourceFormat: data.sourceFormat ?? (selected.name.toLowerCase().endsWith('.pdf') ? 'pdf' : 'markdown'),
        fileName: selected.name,
      };
      onClose();
      navigate(`/podcasts/${podcast.id}/import-document`, { state });
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'message' in err && typeof (err as { message: string }).message === 'string'
          ? (err as { message: string }).message
          : t('podcasts.pdfImport.previewError');
      toast.error(msg);
      setFile(null);
    } finally {
      setLoading(false);
      e.target.value = '';
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="podcast-pdf-import-title"
    >
      <div className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl dark:border-[hsl(217,33%,22%)] dark:bg-[hsl(217,32%,15%)]">
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4 dark:border-gray-700">
          <div>
            <h2 id="podcast-pdf-import-title" className="text-lg font-bold text-slate-900 dark:text-white">
              {t('podcasts.pdfImport.title')}
            </h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-gray-400">
              {t('podcasts.pdfImport.subtitle', { podcast: podcast.title })}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-gray-800"
            aria-label={t('podcasts.actions.close')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <p className="text-sm leading-relaxed text-slate-600 dark:text-gray-300">
            {t('podcasts.pdfImport.hintEditorPage')}
          </p>
          <div
            className={cn(
              'rounded-xl border-2 border-dashed px-4 py-8 text-center transition-colors',
              file ? 'border-teal-500/50 bg-teal-50/50 dark:bg-teal-900/10' : 'border-slate-200 dark:border-gray-600',
            )}
          >
            <input
              ref={inputRef}
              type="file"
              accept={IMPORT_ACCEPT}
              className="sr-only"
              onChange={(e) => void onFileChange(e)}
            />
            <FileUp className="mx-auto h-10 w-10 text-slate-400" aria-hidden />
            <p className="mt-2 text-sm font-medium text-slate-700 dark:text-gray-200">
              {file ? file.name : t('podcasts.pdfImport.dropLabel')}
            </p>
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={loading}
              className="mt-3 rounded-lg bg-[color:var(--inst-player-primary,#0C1C38)] px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              {t('podcasts.pdfImport.chooseFile')}
            </button>
          </div>
          {loading && (
            <div className="flex items-center justify-center gap-2 py-4 text-sm text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin" />
              {t('podcasts.pdfImport.convertingPdf')}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-4 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium dark:border-gray-600"
          >
            {t('podcasts.actions.cancel')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
